import React, { useState, useMemo, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  useTheme,
  alpha,
  Chip,
  Avatar,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useDashboard } from '../../contexts/DashboardContext';
import api from '../../services/api';

dayjs.extend(relativeTime);

// Sample data for agents' last locations
const sampleAgentLocations = [
  {
    id: 1,
    agent_id: 1,
    agent_name: 'John Doe',
    agent_email: 'john.doe@example.com',
    latitude: 19.0760,
    longitude: 72.8777,
    timestamp: dayjs().subtract(5, 'minutes').toISOString(),
    accuracy: 15.5,
    source: 'GPS',
    city: 'Mumbai',
    status: 'active',
  },
  {
    id: 2,
    agent_id: 2,
    agent_name: 'Jane Smith',
    agent_email: 'jane.smith@example.com',
    latitude: 28.7041,
    longitude: 77.1025,
    timestamp: dayjs().subtract(15, 'minutes').toISOString(),
    accuracy: 22.3,
    source: 'GPS',
    city: 'Delhi',
    status: 'active',
  },
  {
    id: 3,
    agent_id: 3,
    agent_name: 'Mike Johnson',
    agent_email: 'mike.johnson@example.com',
    latitude: 12.9716,
    longitude: 77.5946,
    timestamp: dayjs().subtract(45, 'minutes').toISOString(),
    accuracy: 18.7,
    source: 'GPS',
    city: 'Bangalore',
    status: 'active',
  },
  {
    id: 4,
    agent_id: 4,
    agent_name: 'Sarah Williams',
    agent_email: 'sarah.williams@example.com',
    latitude: 17.3850,
    longitude: 78.4867,
    timestamp: dayjs().subtract(2, 'hours').toISOString(),
    accuracy: 25.1,
    source: 'network',
    city: 'Hyderabad',
    status: 'active',
  },
  {
    id: 5,
    agent_id: 5,
    agent_name: 'David Brown',
    agent_email: 'david.brown@example.com',
    latitude: 13.0827,
    longitude: 80.2707,
    timestamp: dayjs().subtract(4, 'hours').toISOString(),
    accuracy: 30.2,
    source: 'GPS',
    city: 'Chennai',
    status: 'active',
  },
  {
    id: 6,
    agent_id: 6,
    agent_name: 'Emily Davis',
    agent_email: 'emily.davis@example.com',
    latitude: 22.5726,
    longitude: 88.3639,
    timestamp: dayjs().subtract(1, 'day').toISOString(),
    accuracy: 35.5,
    source: 'manual',
    city: 'Kolkata',
    status: 'inactive',
  },
];

// Get color based on time since last update
const getLocationColor = (timestamp) => {
  const minutesAgo = dayjs().diff(dayjs(timestamp), 'minute');
  
  if (minutesAgo <= 15) {
    return '#22c55e'; // Green - Very recent (active)
  } else if (minutesAgo <= 60) {
    return '#facc15'; // Yellow - Recent (moderately active)
  } else if (minutesAgo <= 240) {
    return '#f97316'; // Orange - Somewhat old (less active)
  } else {
    return '#ef4444'; // Red - Old (inactive)
  }
};

// Get status label
const getStatusLabel = (timestamp) => {
  const minutesAgo = dayjs().diff(dayjs(timestamp), 'minute');
  
  if (minutesAgo <= 15) {
    return 'Active';
  } else if (minutesAgo <= 60) {
    return 'Recent';
  } else if (minutesAgo <= 240) {
    return 'Inactive';
  } else {
    return 'Offline';
  }
};

const LiveAgentLocation = ({ refreshKey = 0 }) => {
  const theme = useTheme();
  const { fetchAgentLocations, resetDuplicateCallPrevention } = useDashboard();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadMap, setLoadMap] = useState(false);

  // Fetch agent locations on component mount and when refreshKey changes
  useEffect(() => {
    let isMounted = true;
    
    const loadAgentLocations = async () => {
      setLoading(true);
      try {
        // Reset duplicate call prevention to allow fresh API calls
        if (resetDuplicateCallPrevention) {
          resetDuplicateCallPrevention();
        }
        
        const response = await fetchAgentLocations();
        
        // Only update state if component is still mounted
        if (!isMounted) return;
        
        if (response && response.locations) {
          setLocations(response.locations);
        } else {
          // Fallback to empty array instead of sample data for cleaner UX
          setLocations([]);
        }
      } catch (error) {
        console.error('Error fetching agent locations:', error);
        // Only update state if component is still mounted
        if (!isMounted) return;
        // Set empty array on error
        setLocations([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAgentLocations();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]); // Only depend on refreshKey to prevent infinite loops

  // Filter locations based on selected tab
  const filteredLocations = useMemo(() => {
    if (selectedTab === 'all') {
      return locations;
    }
    
    return locations.filter((location) => {
      const minutesAgo = dayjs().diff(dayjs(location.timestamp), 'minute');
      
      switch (selectedTab) {
        case 'active':
          return minutesAgo <= 15;
        case 'recent':
          return minutesAgo > 15 && minutesAgo <= 60;
        case 'inactive':
          return minutesAgo > 60 && minutesAgo <= 240;
        case 'offline':
          return minutesAgo > 240;
        default:
          return true;
      }
    });
  }, [locations, selectedTab]);

  // Calculate counts for each tab
  const tabCounts = useMemo(() => {
    const counts = {
      all: locations.length,
      active: 0,
      recent: 0,
      inactive: 0,
      offline: 0,
    };
    
    locations.forEach((location) => {
      const minutesAgo = dayjs().diff(dayjs(location.timestamp), 'minute');
      if (minutesAgo <= 15) {
        counts.active++;
      } else if (minutesAgo <= 60) {
        counts.recent++;
      } else if (minutesAgo <= 240) {
        counts.inactive++;
      } else {
        counts.offline++;
      }
    });
    
    return counts;
  }, [locations]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleCardClick = (location) => {
    setSelectedLocation(location);
    setDialogOpen(true);
    // Lazy load map after a short delay to let dialog open smoothly
    setTimeout(() => setLoadMap(true), 300);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLocation(null);
    setLoadMap(false);
  };

  const handleOpenInGoogleMaps = () => {
    if (selectedLocation?.latitude && selectedLocation?.longitude) {
      const url = `https://www.google.com/maps?q=${selectedLocation.latitude},${selectedLocation.longitude}`;
      window.open(url, '_blank');
    }
  };

  // Skeleton UI component for loading state
  const SkeletonCard = () => (
    <Box sx={{ minWidth: 320, flexShrink: 0 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderLeft: `4px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="80%" height={16} sx={{ mt: 0.5 }} />
            <Skeleton variant="rounded" width={120} height={22} sx={{ mt: 1 }} />
          </Box>
        </Box>
      </Paper>
    </Box>
  );

  if (loading) {
    return (
      <Box>
        {/* Skeleton Tabs */}
        <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, pb: 1.5 }}>
            <Skeleton variant="rounded" width={80} height={36} />
            <Skeleton variant="rounded" width={120} height={36} />
            <Skeleton variant="rounded" width={130} height={36} />
            <Skeleton variant="rounded" width={130} height={36} />
            <Skeleton variant="rounded" width={120} height={36} />
          </Box>
        </Box>

        {/* Skeleton Cards */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 1,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Tabs */}
      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
            },
            '& .Mui-selected': {
              fontWeight: 600,
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">All</Typography>
                <Chip
                  label={tabCounts.all}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                />
              </Box>
            }
            value="all"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#22c55e',
                  }}
                />
                <Typography variant="body2">Active</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  (≤15 min)
                </Typography>
                <Chip
                  label={tabCounts.active}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: alpha('#22c55e', 0.1),
                    color: '#22c55e',
                  }}
                />
              </Box>
            }
            value="active"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#facc15',
                  }}
                />
                <Typography variant="body2">Recent</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  (15-60 min)
                </Typography>
                <Chip
                  label={tabCounts.recent}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: alpha('#facc15', 0.1),
                    color: '#facc15',
                  }}
                />
              </Box>
            }
            value="recent"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#f97316',
                  }}
                />
                <Typography variant="body2">Inactive</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  (1-4 hrs)
                </Typography>
                <Chip
                  label={tabCounts.inactive}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: alpha('#f97316', 0.1),
                    color: '#f97316',
                  }}
                />
              </Box>
            }
            value="inactive"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#ef4444',
                  }}
                />
                <Typography variant="body2">Offline</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  (>4 hrs)
                </Typography>
                <Chip
                  label={tabCounts.offline}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: alpha('#ef4444', 0.1),
                    color: '#ef4444',
                  }}
                />
              </Box>
            }
            value="offline"
          />
        </Tabs>
      </Box>

      {/* Agent List */}
      {filteredLocations.length === 0 ? (
        <Box
          sx={{
            py: 8,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body1">No agents found in this category</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha(theme.palette.divider, 0.1),
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.divider, 0.3),
              borderRadius: 4,
              '&:hover': {
                backgroundColor: alpha(theme.palette.divider, 0.5),
              },
            },
          }}
        >
          {filteredLocations.map((location) => {
          const color = getLocationColor(location.timestamp);
          const statusLabel = getStatusLabel(location.timestamp);
          
          return (
            <Box
              key={location.id}
              sx={{
                minWidth: 320,
                flexShrink: 0,
              }}
            >
              <Paper
                elevation={0}
                onClick={() => handleCardClick(location)}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `2px solid ${alpha(color, 0.3)}`,
                  borderLeft: `4px solid ${color}`,
                  background: `linear-gradient(to right, ${alpha(color, 0.05)}, ${alpha(color, 0.02)}, background.paper)`,
                  boxShadow: `0 2px 8px ${alpha(color, 0.15)}`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  height: '100%',
                  position: 'relative',
                  '&:hover': {
                    boxShadow: `0 4px 16px ${alpha(color, 0.25)}`,
                    transform: 'translateY(-2px)',
                    borderColor: alpha(color, 0.5),
                  },
                }}
              >
                {/* Color dot in top right */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: color,
                    border: `2px solid white`,
                    boxShadow: `0 2px 4px ${alpha(color, 0.3)}`,
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(color, 0.1),
                      color: color,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {location.agent_name}
                      </Typography>
                      {location.team_name && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.7rem',
                            fontStyle: 'italic',
                          }}
                        >
                          {location.team_name}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {dayjs(location.timestamp).format('MMM DD, YYYY hh:mm A')}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<LocationIcon sx={{ fontSize: 14 }} />}
                        label={
                          location.locality || location.address || location.city || 
                          `${location.latitude?.toFixed(4)}, ${location.longitude?.toFixed(4)}`
                        }
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          fontSize: '0.7rem',
                          height: 22,
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                        title={
                          location.address || 
                          `${location.locality || ''}, ${location.city || ''}`.trim() || 
                          `Lat: ${location.latitude}, Lng: ${location.longitude}`
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
          );
        })}
        </Box>
      )}

      {/* Location Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Agent Location Details
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDialog}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: 'error.main',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {selectedLocation && (
            <Box>
              {/* Agent Name */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                  Agent Name
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedLocation.agent_name}
                </Typography>
                {selectedLocation.team_name && (
                  <Chip
                    label={selectedLocation.team_name}
                    size="small"
                    sx={{
                      mt: 1,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: 'info.main',
                      fontSize: '0.75rem',
                    }}
                  />
                )}
              </Box>

              {/* Last Location */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Last Location
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 1 }}>
                    <LocationIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {selectedLocation.address || selectedLocation.locality || selectedLocation.city || 'Location'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        Coordinates: {selectedLocation.latitude?.toFixed(6)}, {selectedLocation.longitude?.toFixed(6)}
                      </Typography>
                      {selectedLocation.accuracy && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                          Accuracy: ±{selectedLocation.accuracy.toFixed(2)}m
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    <TimeIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {dayjs(selectedLocation.timestamp).format('MMM DD, YYYY hh:mm A')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      ({dayjs(selectedLocation.timestamp).fromNow()})
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1.5 }}>
                    <Chip
                      label={getStatusLabel(selectedLocation.timestamp)}
                      size="small"
                      sx={{
                        bgcolor: alpha(getLocationColor(selectedLocation.timestamp), 0.1),
                        color: getLocationColor(selectedLocation.timestamp),
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Embedded Map - Lazy Loaded */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Map
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: 300,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  {loadMap ? (
                    <iframe
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      style={{ border: 0 }}
                      src={`https://www.google.com/maps?q=${selectedLocation.latitude},${selectedLocation.longitude}&output=embed&z=15`}
                      allowFullScreen
                      title="Agent Location Map"
                    />
                  ) : (
                    <CircularProgress size={40} />
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleOpenInGoogleMaps}
            variant="contained"
            startIcon={<OpenInNewIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Open in Google Maps
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(LiveAgentLocation);
